/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */
import { StreamType } from '../type/StreamType';
import { UserJourneyData } from './UserJourney';

export interface Content extends UserJourneyData {
  /**
   * Unique identifier for content item
   */
  id?: string;
  /**
   * Content type
   */
  type?: string;
  /**
   * Content name from CMS to ensure accurate title if different from the player title
   */
  name?: string;
  /**
   * Content name from CMS to ensure accurate title if different from the player title
   */
  providerId?: string;
  /**
   * Genre of the content
   */
  genre?: string;
  /**
   * From content metadata, the license window start date
   */
  licenseWindowStartDate?: string;
  /**
   * Stream type VOD, live or unknown
   */
  isLive?: StreamType;
  /**
   * Total stream duration
   */
  duration?: number;
  /**
   * Stream URL
   */
  streamUrl?: string;
  /**
   * Encoded framerate
   */
  encodedFramerate?: number;
  /**
   * Viewer unique identifier
   */
  viewerId?: string;
  /**
   * Default resource
   */
  defaultResource?: string;
  /**
   * Channel name
   */
  channel?: string;
  /**
   * The name of the brand to which the content belongs
   */
  brand?: string;
  /**
   * Affiliate or MVPD name for TV Everywhere authenticated services
   */
  affiliate?: string;
  /**
   * Content business categories of interest
   */
  categoryType?: string;
  /**
   * Name of CMS Provider
   */
  assetProvider?: string;
  /**
   * Series identifier only if the metadata cannot be gathered from CMS System.
   */
  seriesId?: string;
  /**
   * Series name only if the metadata cannot be gathered from CMS System.
   */
  seriesName?: string;
  /**
   * Season number only if the details cannot be inferred from Asset Provider Server. Null if not applicable.
   */
  seasonNumber?: string;
  /**
   * Episode/show title only if the details cannot be inferred from Asset Provider Server.
   */
  showTitle?: string;
  /**
   * Episode Number only if the details cannot be inferred from Asset Provider Server. Null if not applicable.
   */
  episodeNumber?: string;
  /**
   * Unique identifier of the playlist associated with the content.
   */
  playlistId?: string;
  /**
   * Name of the playlist associated with the content.
   */
  playlistName?: string;
  /**
   * A list of applicable content genre in a comma separated list only if the details cannot be inferred from Asset Provider Server
   */
  genreList?: string;
  /**
   * Provide your full generated UTM URL/ Link to track the effectiveness of the online marketing campaign across traffic sources and publishing media
   */
  utmUrl?: string;
  /**
   * Unique identifier for the channel.
   */
  channelId?: string;
  /**
   * Unique identifier for the program.
   */
  programId?: string;
  /**
   * Name of the program.
   */
  programName?: string;
  /**
   * Indicates if the content is available as a free preview (true or false).
   */
  isFreePreview?: boolean;
  /**
   * Indicates if the content is set for auto preview (true or false).
   */
  isAutoPreview?: boolean;
  /**
   * External identifier for the content, typically from a third-party or external system
   */
  externalContentId?: string;
  /**
   * Identifier for enrichment content, used for linking additional metadata or related assets
   */
  enrichmentContentId?: string;
  /**
   * Numeric identifier for the content, typically used for internal tracking or database indexing.
   */
  numericContentId?: number;
  /**
   * Language of the content
   */
  contentLanguage?: string;
  /**
   * Playback mode of the content
   */
  playbackMode?: string;
  /**
   * Specific package Name.
   */
  packageName?: string;
  /**
   * Collection of related films
   */
  saga?: string;
  /**
   * Distinguish the consumption of customers with different resolution contracts (e.g.,SD, HD or UHD qualities).
   */
  contractedResolution?: string;
  /**
   * Subtitle language or format associated with the content
   */
  subtitles?: string;
  /**
   * Filter by the different encoding settings the customer has set in the encoder.
   */
  codecSettings?: string;
  /**
   * Codec profiles target specific classes of application. Filter your views by their codec profile to see content with specific requirements.
   */
  codecProfile?: string;

  /**
   * Defines the playback experience classification used for analytics reporting.
   *
   * These values are used to override the NPAW `content_playbacktype`
   * dimension from the application layer, ensuring consistent playback
   * type reporting across different stream types and viewing experiences.
   */

  playbackType?: PlaybackType;

  /**
   * Custom metadata key-value pairs associated with the content
   */
  customMetadata?: { [key: string]: any };
}

export enum PlaybackType {
  VOD = 'VOD',
  LIVE = 'LIVE',
  DVR = 'DVR',
  CATCHUP = 'CATCHUP',
}
